import { useEffect, useState } from "react";
import axios from "axios";

/**
 * Lightweight ops-health indicator for the nav bar.
 *
 * Polls /api/readyz every 60s. Renders a single pulsing dot:
 *   - green  → 200 from /readyz (Mongo reachable, ready to serve traffic)
 *   - amber  → 503 from /readyz (process up, but Mongo unreachable — readiness probe failing)
 *   - red    → request failed entirely (network down / backend dead)
 *
 * Uses a bare axios client (no withCredentials, no shared interceptor) so a
 * 401 here doesn't kick the global refresh-token interceptor. The probe is
 * intentionally public on the backend.
 *
 * Hidden until the first probe response lands to avoid a flash of "red"
 * on cold start.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const PROBE_URL = `${BACKEND}/api/readyz`;
const POLL_MS = 60_000;

const STATES = {
  ok: { color: "bg-[var(--rl-success)]", label: "All systems operational", testid: "system-health-ok" },
  degraded: { color: "bg-[var(--rl-warning,#FFB800)]", label: "Degraded — database lag detected", testid: "system-health-degraded" },
  down: { color: "bg-[var(--rl-accent)]", label: "Backend unreachable", testid: "system-health-down" },
};

export default function SystemHealthDot() {
  const [status, setStatus] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const { status: code, data } = await axios.get(PROBE_URL, { timeout: 4000 });
        if (cancelled) return;
        // /readyz returns 200 {status:"ready"} when Mongo is reachable.
        // FastAPI mirrors 503 + {status:"unavailable"} when Mongo is down.
        if (code === 200 && data?.status === "ready") setStatus("ok");
        else setStatus("degraded");
      } catch (err) {
        if (cancelled) return;
        // Distinguish 503 (backend up, Mongo down) from true network failure.
        const code = err?.response?.status;
        setStatus(code === 503 ? "degraded" : "down");
      }
    };
    probe();
    const t = setInterval(probe, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!status) return null;
  const meta = STATES[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]"
      title={meta.label}
      data-testid="system-health-dot"
      data-status={status}
    >
      <span
        className={`relative w-2 h-2 rounded-full ${meta.color}`}
        aria-hidden="true"
        data-testid={meta.testid}
      >
        {status === "ok" && (
          <span
            className={`absolute inset-0 rounded-full ${meta.color} animate-ping opacity-40`}
            aria-hidden="true"
          />
        )}
      </span>
      <span className="sr-only">{meta.label}</span>
    </span>
  );
}
